import { Row, Col, Container } from "react-bootstrap";
import {
  ListTable,
  ModalMessage,
  LoadingSpinner,
  PaginationMenu,
  SortDropdown,
} from "../../components";
import { fetchOrdersApi } from "../../api/ordersApi.js";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getStorageToken, useLogout } from "../../utilities";

export default function UserOrdersList() {
  // displays a list of all orders the user has placed
  // user can sort/filter the list

  const tableHeaders = ["Date", "Total", "Paid", "Delivered", "Cancelled"];
  const fieldNames = [
    "createdAt",
    ["orderTotal", "cartSubtotal"],
    "isPaid",
    "isDelivered",
    "isCancelled",
  ];

  const initialPaginationState = {
    pageNr: 1,
    totalNrOfPages: 1,
  };

  const sortOptions = [
    { value: "createdAt_-1", label: "Date: Newest" },
    { value: "createdAt_1", label: "Date: Oldest" },
    { value: "orderTotal.cartSubtotal_-1", label: "Total: High To Low" },
    { value: "orderTotal.cartSubtotal_1", label: "Total: Low To High" },
  ];

  const filterOptions = [
    { value: "", label: "None" },
    { value: "isPaid_1", label: "Paid" },
    { value: "isPaid_0", label: "Not Paid" },
    { value: "isDelivered_1", label: "Delivered" },
    { value: "isDelivered_0", label: "Not Delivered" },
    { value: "isCancelled_1", label: "Cancelled" },
    { value: "isCancelled_0", label: "Not Cancelled" },
  ];

  const { stayLoggedIn } = useSelector((state) => state.user);
  const logout = useLogout();

  const [orders, setOrders] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [paginationState, setPaginationState] = useState(
    {...initialPaginationState}
  );
  const [sortOption, setSortOption] = useState(sortOptions[0]?.value);
  const [filterOption, setFilterOption] = useState(filterOptions[0]?.value);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchOrders = async (pageNr = null) => {
      // returns array with jsons
      setIsLoading(true);
      try {
        const token = getStorageToken(stayLoggedIn);
        const res = await fetchOrdersApi(
          token,
          abortController.signal,
          pageNr,
          sortOption,
          filterOption
        );
        return res;
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        }
        setErrorMsg("Could not load data. Please try again later.");
      } finally {
        setIsLoading(false);
        window.scrollTo(0, 0);
      }
    };

    const pageNr = paginationState?.pageNr || 1;
    fetchOrders(pageNr)
      .then((res) => {
        setOrders(res?.orders);
        setPaginationState({
          pageNr: res?.currPageNr || 1,
          totalNrOfPages: res?.totalNrOfPages || 1,
        });
      })
      .catch((error) => {
        setErrorMsg("Could not load data. Please try again later.");
      });
    return () => abortController.abort();
  }, [filterOption, sortOption, paginationState?.pageNr, stayLoggedIn, logout]);

  const handleChangePageNr = (newPageNr) => {
    if (newPageNr < 1) {
      newPageNr = 1;
    } else if (newPageNr > paginationState.totalNrOfPages) {
      newPageNr = paginationState.totalNrOfPages;
    }
    setPaginationState({ ...paginationState, pageNr: newPageNr });
  };

  return (
    <Container className="my-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          autoClose={true}
          type="success"
          message={successMsg}
          setSuccessMsg={setSuccessMsg}
        />
      )}
      <Row>
        <Col md={12}>
          <h1>My Orders</h1>
          {isLoading ? <LoadingSpinner /> : null}

          {/* SORT & FILTER MENU */}
          {orders && (
            <Row xs={2} md={3} lg={4} className="mt-5 mb-1">
              <Col>
                <SortDropdown
                  sortOptions={sortOptions}
                  handleChangeSortKey={setSortOption}
                />
              </Col>
              <Col>
                <SortDropdown
                  sortOptions={filterOptions}
                  heading="Filter By"
                  handleChangeSortKey={setFilterOption}
                />
              </Col>
            </Row>
          )}
          {orders && (
            <ListTable
              dataArray={orders}
              headers={tableHeaders}
              fieldNames={fieldNames}
              view={`/user/my-order-details`}
            />
          )}
        </Col>
      </Row>

      {/* PAGINATION MENU */}
      {orders && orders.length > 0 && (
        <PaginationMenu
          pageNr={paginationState.pageNr}
          totalNrOfPages={paginationState.totalNrOfPages}
          handleChangePageNr={handleChangePageNr}
        />
      )}
    </Container>
  );
}
